import React, { useState, useRef } from "react";
import { Mic, MicOff, X, Loader2, Volume2, MessageSquare } from "lucide-react";
import { api } from "@/lib/api";
import { useApp } from "@/context/AppContext";

const LANGS = [
  { code: "en", asr: "en-US", tts: "en-US", label: "English" },
  { code: "ar", asr: "ar-SA", tts: "ar-SA", label: "العربية" },
];

export default function VoiceAssistant() {
  const { t, lang } = useApp();
  const [open, setOpen] = useState(false);
  const [listening, setListening] = useState(false);
  const [inLang, setInLang] = useState(lang);
  const [outLang, setOutLang] = useState(lang);
  const [transcript, setTranscript] = useState("");
  const [response, setResponse] = useState("");
  const [loading, setLoading] = useState(false);
  const [supported] = useState(() => typeof window !== "undefined" && ("webkitSpeechRecognition" in window || "SpeechRecognition" in window));
  const recRef = useRef(null);

  const speak = (text, code) => {
    if (!("speechSynthesis" in window)) return;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    u.lang = LANGS.find((l) => l.code === code)?.tts || "en-US";
    window.speechSynthesis.speak(u);
  };

  const ask = async (text) => {
    setLoading(true); setResponse("");
    try {
      const outName = LANGS.find((l) => l.code === outLang)?.label;
      const { data } = await api.post("/sandbox/run", {
        prompt: text,
        system_prompt: `You are a concise multilingual enterprise AI voice assistant. Answer in ${outName}. Keep it under 5 sentences.`,
      });
      setResponse(data.output);
      speak(data.output, outLang);
    } catch {
      setResponse("Assistant unavailable right now. Please try again.");
    } finally { setLoading(false); }
  };

  const startListening = () => {
    if (!supported) return;
    const SR = window.SpeechRecognition || window.webkitSpeechRecognition;
    const rec = new SR();
    rec.lang = LANGS.find((l) => l.code === inLang)?.asr || "en-US";
    rec.interimResults = false;
    rec.onresult = (e) => { const txt = e.results[0][0].transcript; setTranscript(txt); ask(txt); };
    rec.onend = () => setListening(false);
    rec.onerror = () => setListening(false);
    recRef.current = rec;
    rec.start();
    setListening(true);
  };
  const stopListening = () => { recRef.current?.stop(); setListening(false); };

  return (
    <>
      <button data-testid="voice-assistant-fab" onClick={() => setOpen((o) => !o)}
        className="fixed bottom-6 z-50 flex h-14 w-14 items-center justify-center rounded-full bg-accent text-accent-foreground shadow-lg shadow-accent/30 hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-ring"
        style={{ insetInlineEnd: "1.5rem" }} aria-label={t("voiceAssistant")}>
        <Mic className="h-6 w-6" />
      </button>
      {open && (
        <div data-testid="voice-assistant-panel" className="fixed bottom-24 z-50 w-[92vw] max-w-sm rounded-2xl border border-border glass p-4 shadow-2xl fade-in" style={{ insetInlineEnd: "1.5rem" }}>
          <div className="mb-3 flex items-center justify-between">
            <h3 className="flex items-center gap-2 text-sm font-semibold"><MessageSquare className="h-4 w-4 text-accent" /> {t("voiceAssistant")}</h3>
            <button onClick={() => setOpen(false)} data-testid="voice-close"><X className="h-4 w-4" /></button>
          </div>
          <div className="mb-3 grid grid-cols-2 gap-2 text-xs">
            <label className="flex flex-col gap-1">In
              <select data-testid="voice-in-lang" value={inLang} onChange={(e) => setInLang(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
            <label className="flex flex-col gap-1">Out
              <select data-testid="voice-out-lang" value={outLang} onChange={(e) => setOutLang(e.target.value)} className="rounded-lg border border-border bg-background px-2 py-1">
                {LANGS.map((l) => <option key={l.code} value={l.code}>{l.label}</option>)}
              </select>
            </label>
          </div>
          {supported ? (
            <button data-testid="voice-mic-btn" onClick={listening ? stopListening : startListening}
              className={`mb-3 flex w-full items-center justify-center gap-2 rounded-xl px-3 py-2.5 text-sm font-semibold ${listening ? "bg-destructive text-destructive-foreground" : "bg-primary text-primary-foreground"}`}>
              {listening ? <><MicOff className="h-4 w-4" /> Stop</> : <><Mic className="h-4 w-4" /> Speak</>}
            </button>
          ) : (
            <p className="mb-2 text-xs text-amber-300">Voice input not supported here — type below.</p>
          )}
          <input data-testid="voice-text-input" placeholder="Type a message…" className="mb-2 w-full rounded-xl border border-border bg-background px-3 py-2 text-sm"
            onKeyDown={(e) => { if (e.key === "Enter" && e.target.value.trim()) { setTranscript(e.target.value); ask(e.target.value); e.target.value = ""; } }} />
          {transcript && <div className="mb-2 text-xs text-muted-foreground"><b>{t("transcript")}:</b> {transcript}</div>}
          <div className="min-h-[48px] rounded-xl border border-border bg-secondary/40 p-2 text-sm">
            {loading ? <Loader2 className="h-4 w-4 animate-spin" /> : (response ? <span className="flex gap-2"><Volume2 className="h-4 w-4 shrink-0 text-accent" /> {response}</span> : <span className="text-xs text-muted-foreground">{t("response")}…</span>)}
          </div>
        </div>
      )}
    </>
  );
}
